import { useMutation } from '@tanstack/react-query';
import { hasActiveSubscriptionPlans, useSubscriptionDetailsStore, useUserDetailsStore } from '@zeta-core';
import { AdvisorAPIs, BillingService, CreditsService } from '@zeta-services';
import { ICreditBalanceResponse, IFlexiblePlanStatusResponse, IGetAdvisorPlanAssignmentsResponse } from '@zeta-types';
import { useEffect, useState } from 'react';

export const useFlexiblePlanAccess = () => {
  const currentSubscription = useSubscriptionDetailsStore((state) => state.subscription);
  const subscriptionLoading = useSubscriptionDetailsStore((state) => state.loading);
  const userDetails = useUserDetailsStore((state) => state.user);
  const [hasFirmWalletAccess, setHasFirmWalletAccess] = useState(false);
  const hasActivePlan = hasActiveSubscriptionPlans(currentSubscription);

  const getCreditBalance = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled([
        CreditsService().getCreditBalance({ advisorFirmId: userDetails?.accessibleEntityID, advisorId: userDetails?.id }),
        AdvisorAPIs().getAdvisorPlanAssignments({ advisorID: userDetails?.accessibleEntityID || '' }),
        BillingService().getFlexiblePlanStatus({ advisorId: userDetails?.accessibleEntityID || '' }),
      ]);

      return {
        creditBalance: results[0].status === 'fulfilled' ? (results[0].value.data as ICreditBalanceResponse) : null,
        planAssignments: results[1].status === 'fulfilled' ? (results[1].value.data as IGetAdvisorPlanAssignmentsResponse) : null,
        flexiblePlanStatus: results[2].status === 'fulfilled' ? (results[2].value.data as IFlexiblePlanStatusResponse) : null,
      };
    },
    onSuccess: (response: {
      creditBalance: ICreditBalanceResponse | null;
      planAssignments: IGetAdvisorPlanAssignmentsResponse | null;
      flexiblePlanStatus: IFlexiblePlanStatusResponse | null;
    }) => {
      const totalAmount = response?.creditBalance?.totalAmount || 0;
      const availableAmount = response?.creditBalance?.availableAmount || 0;
      const hasWalletBalance = totalAmount > 0 || availableAmount > 0;
      const hasFlexiblePlanAssignment =
        response?.planAssignments?.plans?.some((plan) => {
          const planName = (plan?.planName || '').toLowerCase();
          return planName.includes('flex plan') || planName.includes('flexible');
        }) || false;
      const hasActiveFlexiblePlan = response?.flexiblePlanStatus?.active || false;

      setHasFirmWalletAccess(hasWalletBalance || hasFlexiblePlanAssignment || hasActiveFlexiblePlan);
    },
    onError: () => {
      setHasFirmWalletAccess(false);
    },
  });

  useEffect(() => {
    if (subscriptionLoading) {
      return;
    }

    if (hasActivePlan) {
      setHasFirmWalletAccess(false);
      return;
    }

    if (userDetails?.accessibleEntityID && userDetails?.id) {
      getCreditBalance.mutate();
      return;
    }

    setHasFirmWalletAccess(false);
  }, [subscriptionLoading, hasActivePlan, userDetails?.accessibleEntityID, userDetails?.id]);

  return {
    hasActivePlan,
    hasFlexiblePlanAccess: hasActivePlan || hasFirmWalletAccess,
    isFlexiblePlanAccessLoading: !hasActivePlan && getCreditBalance.isPending,
  };
};
